import { GeoJSON, CircleMarker, Polyline } from 'react-leaflet';
import L from 'leaflet';

interface TrackLayersProps {
  geojson: GeoJSON.FeatureCollection | null;
  socketRef?: React.MutableRefObject<WebSocket | null>;
  usernameCache?: Map<string, string>;
  onRegisterPendingRequest?: (requestId: string, data: any) => void;
}

export function TrackLayers({ geojson, socketRef, usernameCache = new Map(), onRegisterPendingRequest }: TrackLayersProps) {
  if (!geojson || !geojson.features || geojson.features.length === 0) {
    return null;
  }

  // Function to query username from server
  const fetchUsername = (userId: string, layer: any) => {
    // Check cache first
    if (usernameCache.has(userId)) {
      const username = usernameCache.get(userId);
      layer.bindPopup(`Track by: ${username}`).openPopup();
      return;
    }

    // Generate a unique request ID
    const requestId = `${Date.now()}_${Math.random()}`;
    
    // Register the pending request
    if (onRegisterPendingRequest) {
      onRegisterPendingRequest(requestId, { layer, userId });
    }

    // Send query to server
    if (socketRef?.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'query_username',
        userId: userId,
        requestId: requestId
      }));
      
      // Show loading popup
      layer.bindPopup('Loading username...').openPopup();
    } else {
      // Fallback if no connection
      layer.bindPopup(`Track by: User_${userId.substring(0, 8)}`).openPopup();
    }
  };

  // Style function for different track elements
  const style = (feature: any) => {
    const { type, isActive } = feature.properties;
    
    switch (type) {
      case 'track_line':
        return {
          color: isActive ? '#ff6b35' : '#4a90e2', // Orange for active, blue for completed
          weight: 3,
          opacity: 0.8,
          fillOpacity: 0
        };
      case 'track_point':
        return {
          color: isActive ? '#ff6b35' : '#4a90e2',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.7,
          radius: 6
        };
      case 'track_start':
        return {
          color: '#2ecc71', // Green for start
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8,
          radius: 8
        };
      case 'track_end':
        return {
          color: '#e74c3c', // Red for end
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8,
          radius: 8
        };
      default:
        return {
          color: '#999999',
          weight: 2,
          opacity: 0.6
        };
    }
  };

  // Custom point to layer function for markers
  const pointToLayer = (feature: any, latlng: L.LatLng) => {
    const styleOptions = style(feature);
    
    return L.circleMarker(latlng, {
      radius: styleOptions.radius || 6,
      color: styleOptions.color,
      weight: styleOptions.weight,
      opacity: styleOptions.opacity,
      fillOpacity: styleOptions.fillOpacity
    });
  };

  // Handle feature interactions and popups
  const onEachFeature = (feature: any, layer: any) => {
    const { userId, trackName, isActive, startedAt, endedAt, pointCount, type } = feature.properties;
    
    // Set initial popup content based on cache
    const cachedUsername = usernameCache.get(userId);
    let popupContent = '';
    
    if (cachedUsername) {
      switch (type) {
        case 'track_line':
          popupContent = `Track by: ${cachedUsername}\nName: ${trackName}\nPoints: ${pointCount}\nStatus: ${isActive ? 'Active' : 'Completed'}\nStarted: ${new Date(startedAt).toLocaleString()}`;
          if (endedAt) {
            popupContent += `\nEnded: ${new Date(endedAt).toLocaleString()}`;
          }
          break;
        case 'track_point':
          popupContent = `Single point by: ${cachedUsername}\nName: ${trackName}\nStatus: ${isActive ? 'Active' : 'Completed'}\nTime: ${new Date(startedAt).toLocaleString()}`;
          break;
        case 'track_start':
          popupContent = `Start point\nTrack by: ${cachedUsername}\nName: ${trackName}\nStarted: ${new Date(startedAt).toLocaleString()}`;
          break;
        case 'track_end':
          popupContent = `End point\nTrack by: ${cachedUsername}\nName: ${trackName}\nEnded: ${new Date(endedAt).toLocaleString()}`;
          break;
        default:
          popupContent = `Track by: ${cachedUsername}`;
      }
      layer.bindPopup(popupContent.replace(/\n/g, '<br>'));
    } else {
      layer.bindPopup('Click to see track details');
    }
    
    layer.on({
      click: () => {
        if (userId) {
          fetchUsername(userId, layer);
        } else {
          layer.openPopup();
        }
      }
    });
  };

  // Simple hash function for generating consistent keys
  const simpleHash = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  };

  // Create a key by hashing the complete GeoJSON structure
  const geoJsonKey = simpleHash(JSON.stringify({
    features: geojson.features.map(f => ({
      id: f.properties?.id,
      type: f.properties?.type,
      pointCount: f.properties?.pointCount,
      isActive: f.properties?.isActive,
      userId: f.properties?.userId,
      geometry: f.geometry,
      cachedUsername: f.properties?.userId ? usernameCache.get(f.properties.userId) : null
    }))
  }));
  
  return (
    <GeoJSON
      key={geoJsonKey} // Re-render when track data or cached usernames change
      data={geojson}
      style={style}
      pointToLayer={pointToLayer}
      onEachFeature={onEachFeature}
      pmIgnore={true} // Prevent editing of track layers
    />
  );
}

export default TrackLayers;